import { Component, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Checkbox Component
 * 
 * Accessible checkbox component with label, description, and indeterminate state support.
 * 
 * @example
 * ```typescript
 * // In your component
 * isChecked = signal(false);
 * ```
 * 
 * ```html
 * <mg3p-checkbox
 *   label="Aceito os termos"
 *   [checked]="isChecked()"
 *   (checkedChange)="isChecked.set($event)"
 *   description="Li e concordo com os termos de uso"
 * />
 * ```
 * 
 * Features:
 * - Checked, unchecked, and indeterminate states
 * - Label and description
 * - Disabled state
 * - Error state
 * - Keyboard accessible (Space, Enter)
 * - WCAG 2.1 Level AA compliant
 */
@Component({
  selector: 'mg3p-checkbox',
  standalone: true,
  imports: [CommonModule],
  template: `
    <label
      class="checkbox"
      [class.checkbox--checked]="checked()"
      [class.checkbox--indeterminate]="indeterminate()"
      [class.checkbox--disabled]="disabled()"
      [class.checkbox--error]="error()"
    >
      <input
        type="checkbox"
        class="checkbox__input"
        [checked]="checked()"
        [indeterminate]="indeterminate()"
        [disabled]="disabled()"
        [required]="required()"
        (change)="onChange($event)"
        [attr.aria-label]="ariaLabel() || label()"
        [attr.aria-describedby]="description() ? checkboxId() + '-desc' : null"
        [attr.aria-invalid]="error()"
      />
      
      <span class="checkbox__box" aria-hidden="true">
        @if (checked()) {
          <span class="checkbox__check">✓</span>
        } @else if (indeterminate()) {
          <span class="checkbox__indeterminate">−</span>
        }
      </span>

      @if (label() || description()) {
        <span class="checkbox__content">
          @if (label()) {
            <span class="checkbox__label">
              {{ label() }}
              @if (required()) {
                <span class="checkbox__required" aria-label="obrigatório">*</span>
              }
            </span>
          }
          @if (description()) {
            <span 
              [id]="checkboxId() + '-desc'"
              class="checkbox__description"
            >
              {{ description() }}
            </span>
          }
        </span>
      }
    </label>

    @if (errorMessage()) {
      <div class="checkbox__error" role="alert">
        <span aria-hidden="true">⚠️</span>
        {{ errorMessage() }}
      </div>
    }
  `,
  styles: [`
    .checkbox {
      display: inline-flex;
      align-items: flex-start;
      gap: var(--spacing-sm);
      cursor: pointer;
      user-select: none;
    }

    .checkbox__input {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }

    .checkbox__box {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      flex-shrink: 0;
      background: var(--color-surface);
      border: 2px solid var(--color-border);
      border-radius: var(--radius-sm);
      transition: all var(--transition-fast);
    }

    .checkbox:hover:not(.checkbox--disabled) .checkbox__box {
      border-color: var(--color-primary);
    }

    .checkbox__input:focus-visible + .checkbox__box {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }

    .checkbox--checked .checkbox__box,
    .checkbox--indeterminate .checkbox__box {
      background: var(--color-primary);
      border-color: var(--color-primary);
    }

    .checkbox__check,
    .checkbox__indeterminate {
      color: white;
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-bold);
      line-height: 1;
    }

    .checkbox__content {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      padding-top: 2px;
    }

    .checkbox__label {
      font-size: var(--font-size-md);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-primary);
    }

    .checkbox__required {
      color: var(--color-danger);
      margin-left: var(--spacing-xs);
    }

    .checkbox__description {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
      line-height: 1.4;
    }

    .checkbox__error {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
      margin-top: var(--spacing-xs);
      font-size: var(--font-size-sm);
      color: var(--color-danger);
    }

    /* Disabled state */
    .checkbox--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* Error state */
    .checkbox--error .checkbox__box {
      border-color: var(--color-danger);
    }

    .checkbox--error.checkbox--checked .checkbox__box,
    .checkbox--error.checkbox--indeterminate .checkbox__box {
      background: var(--color-danger);
      border-color: var(--color-danger);
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .checkbox__box {
        transition: none;
      }
    }
  `]
})
export class CheckboxComponent {
  // Inputs
  label = input<string>('');
  description = input<string>('');
  checked = input<boolean>(false);
  indeterminate = input<boolean>(false);
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  error = input<boolean>(false);
  errorMessage = input<string>('');
  ariaLabel = input<string>('');

  // Outputs
  checkedChange = output<boolean>();

  // State
  checkboxId = signal<string>(`checkbox-${Math.random().toString(36).substr(2, 9)}`);

  onChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.checkedChange.emit(input.checked);
  }
}

// Made with Bob
